import { Box, PopoverPosition, useTheme } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import {
  ExpandableCopyText,
  RobotoCheckbox,
  RobotoErrorAlertDialog,
  RobotoTableCell,
} from "@/shared/components";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal/RobotoPrincipalUnderlineLink.tsx";
import {
  basename,
  FileNode,
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import { FileSystemNodeType } from "@/shared/domain/files/filesystem/fileSystemNode";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { formatByteSize } from "@/shared/text";
import {
  APIServiceError,
  FileSignedUrlResponse,
  fileSignedUrlEndpoint,
} from "@/types";

import { ActionCell } from "./ActionCell";
import { ActionMenu } from "./ActionMenu";
import { ExpandRowIcon } from "./ExpandRowIcon";

interface DatasetFileRowProps {
  isChecked: boolean;
  isRowExpanded: boolean;
  isRowLoading: boolean;
  row: FileSystemNode;
  goToFile: () => void;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  handleRenameNode: () => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  toggleCollapse: () => void;
}

export const DatasetFileRow: React.FC<DatasetFileRowProps> = ({
  isChecked,
  isRowLoading,
  isRowExpanded,
  row,
  goToFile,
  handleActionConfirmation,
  handleDeleteRow,
  handleRowDoubleClick,
  handleRenameNode,
  handleSelectRows,
  onMenuOpen,
  onMenuClose,
  toggleCollapse,
}) => {
  const theme = useTheme();

  const { files: fileService } = useDomainServices();

  const { currentOrganization } = useAuth();
  const [alertOpen, setAlertOpen] = React.useState<boolean>(false);
  const [lastError, setLastError] = React.useState<APIServiceError | undefined>(
    undefined,
  );

  const [anchorPos, setAnchorPos] = React.useState<PopoverPosition>();

  const handlePopoverMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorPos({ top: event.clientY, left: event.clientX });
    onMenuOpen();
  };

  const { initiateRequest: initiateSignedUrlRequest } =
    useLazyAPICall<FileSignedUrlResponse>();

  const handleError = (error: APIServiceError | undefined) => {
    setLastError(error);
    setAlertOpen(true);
  };

  const fetchSignedUrl = async (row: FileNode, forDownload?: boolean) => {
    const file = row.file;
    if (!file) {
      return;
    }

    const { data, error } = await initiateSignedUrlRequest({
      endpoint: fileSignedUrlEndpoint,
      method: "GET",
      orgId: currentOrganization?.org_id,
      queryParams: fileService.getSignedUrlParams(file, forDownload),
      pathParams: { fileId: file.file_id },
    });

    if (!error && data?.data) {
      return data.data.url;
    }

    if (error) {
      handleError(error);
    }
  };

  const rowIsFileType = row?.type === FileSystemNodeType.File;

  const itemDoubleClicked = () => {
    if (row.type === FileSystemNodeType.File) {
      goToFile();
    } else if (row.type === FileSystemNodeType.Directory) {
      handleRowDoubleClick(row);
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    handlePopoverMenu(event);
  };

  return (
    <>
      <RobotoErrorAlertDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        error={lastError}
      />
      <RobotoTableCell
        sx={{
          padding: "0px",
          width: "42.5px",
          maxWidth: "42.5px",
        }}
        onContextMenu={handleContextMenu}
      >
        <RobotoCheckbox
          sx={{
            ml: theme.spacing(1),
            display: {
              xs: "none",
              sm: "inline-flex",
            },
          }}
          checked={isChecked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleSelectRows([row], e.target.checked);
          }}
          color="default"
          inputProps={{ "aria-label": "checkbox" }}
        />
      </RobotoTableCell>

      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.5),
            userSelect: "none",
            wordBreak: "break-all",
          }}
        >
          <ExpandRowIcon
            isExpanderOpen={isRowExpanded}
            isRowLoading={isRowLoading}
            row={row}
            handleIconClick={() => {
              toggleCollapse();
            }}
          />
          <ExpandableCopyText
            text={basename(row.name)}
            trimLength={basename(row.name).length}
          />
        </Box>
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          userSelect: "none",
        }}
      >
        {isFile(row) ? new Date(row.file.modified).toLocaleString() : ""}
        {isDirectory(row)
          ? new Date(row.directory.modified).toLocaleString()
          : ""}
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          minWidth: "10rem",
        }}
      >
        {isFile(row) && (
          <RobotoPrincipalUnderlineLink principal={row.file.created_by} icon />
        )}
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          userSelect: "none",
        }}
      >
        {isFile(row) ? formatByteSize(row.file?.size) : ""}
      </RobotoTableCell>
      <ActionCell
        row={row}
        handleMoreClicked={(e) => {
          e.stopPropagation();
          handlePopoverMenu(e);
        }}
      />

      <ActionMenu
        row={row}
        onDeleteSuccess={() => {
          // Remove row from UI
          handleDeleteRow(row);
        }}
        anchorPos={anchorPos}
        setAnchorPos={setAnchorPos}
        handleActionConfirmation={handleActionConfirmation}
        fetchSignedUrl={(row) => fetchSignedUrl(row, true)}
        openContent={() => {
          goToFile();
        }}
        checked={isChecked}
        handleSelectRows={handleSelectRows}
        detailsExpandable={true}
        openDetails={toggleCollapse}
        handleRenameNode={handleRenameNode}
        rowIsFileType={rowIsFileType}
        onClose={onMenuClose}
      />
    </>
  );
};
